import { ReactNode, useMemo, useState } from 'react';

import { ToolDebugInfo } from '@/components/assistant-ui/tool-debug-info';
import { getToolStatusFlags, ToolStatus } from '@/components/assistant-ui/tool-status';
import { ToolCallInfo } from '@/components/assistant-ui/use-tool-group-state';
import { CarouselTab, CarouselTabs } from '@/components/ui/carousel-tabs';
import { ItemCarousel } from '@/components/ui/item-carousel';

function getActiveTab<T>(
  tabs: CarouselTab<T>[],
  selectedTabId: string | null,
): CarouselTab<T> | undefined {
  const selectedTab = selectedTabId ? tabs.find(t => t.id === selectedTabId) : null;
  return selectedTab ?? tabs.find(t => t.items.length > 0) ?? tabs[0];
}

interface ToolCallsDebugInfoProps {
  toolCalls: ToolCallInfo[];
}

export function ToolCallsDebugInfo({ toolCalls }: ToolCallsDebugInfoProps): ReactNode {
  return (
    <>
      {toolCalls.map(call => (
        <ToolDebugInfo
          key={call.toolCallId}
          toolName={call.toolName}
          args={call.args}
          result={call.result}
          status={call.status}
        >
          {null}
        </ToolDebugInfo>
      ))}
    </>
  );
}

export interface CustomHeaderProps<T> {
  tabs: CarouselTab<T>[];
  selectedTabId: string;
  onSelectTab: (tabId: string) => void;
}

interface TabbedToolUICarouselProps<T> {
  tabs: CarouselTab<T>[];
  renderItem: (item: T) => ReactNode;
  status: ToolStatus;
  loadingUI: ReactNode;
  emptyState: ReactNode;
  beforeCarousel?: ReactNode;
  alwaysShowTabs?: boolean;
  showDots?: boolean;
  className?: string;
  containerClassName?: string;
  /** When true, shows loadingUI below the carousel instead of above, and treats status as complete */
  isLoadingMore?: boolean;
  /** Custom header component that replaces the default CarouselTabs */
  customHeader?: (props: CustomHeaderProps<T>) => ReactNode;
}

export function TabbedToolUICarousel<T>({
  tabs,
  renderItem,
  status,
  loadingUI,
  emptyState,
  beforeCarousel,
  alwaysShowTabs = false,
  showDots: showDotsProp,
  className = 'space-y-4 py-2',
  containerClassName = 'space-y-2',
  isLoadingMore = false,
  customHeader,
}: TabbedToolUICarouselProps<T>): ReactNode {
  const effectiveStatus = isLoadingMore ? { type: 'complete' as const } : status;
  const { isRunning, isError } = getToolStatusFlags(effectiveStatus);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);

  const activeTab = getActiveTab(tabs, selectedTabId);
  const activeTabId = activeTab?.id ?? '';
  const showTabs = alwaysShowTabs || tabs.length > 1;
  const hasItems = tabs.some(t => t.items.length > 0);
  const showDots = showDotsProp ?? true;

  // Stable key for the carousel that changes on thread switch but not tab switch.
  // This forces remount when tabs change (new thread) while preserving fade transitions for tab switches.
  const carouselKey = useMemo(() => tabs.map(t => t.id).join('|'), [tabs]);

  const header = customHeader ? (
    customHeader({ tabs, selectedTabId: activeTabId, onSelectTab: setSelectedTabId })
  ) : showTabs ? (
    <CarouselTabs tabs={tabs} selectedTabId={activeTabId} onSelectTab={setSelectedTabId} />
  ) : undefined;

  return (
    <div className={containerClassName}>
      {!isLoadingMore && loadingUI}
      {(hasItems || !isRunning) && !isError && (
        <>
          {beforeCarousel}
          <ItemCarousel
            key={carouselKey}
            items={activeTab?.items ?? []}
            itemsKey={activeTabId}
            renderItem={renderItem}
            header={header}
            showDots={showDots}
            emptyState={emptyState}
            className={className}
          />
        </>
      )}
      {isLoadingMore && loadingUI}
    </div>
  );
}
